import type { OutreachSource } from "../../../../../hooks/useEmployerOutreachSource";
import type { OutreachType } from "../../../../../hooks/useEmployerOutreachUIConfig";
import type { ResumeEngagement } from "../ResumeEngagementDataBuilder";

import { callRezContactApi, callRezContactGetApi } from "./rezContactApi";
import { logEmployerOutreachApply } from "../../../../../util/employerOutreachSignalsLoggingUtil";

export interface HandleEmployerOutreachApplyParams {
  invite: ResumeEngagement;
  rezcowBaseUrl: string;
  resumeBaseUrl: string;
  jobKey: string;
  outreachSource?: OutreachSource;
  outreachType?: OutreachType;
  accountKey?: string;
  ctk?: string;
  mobtk?: string;
}

/**
 * Handles employer outreach apply flow:
 * 1. Calls the interested endpoint
 * 2. Calls the apply redirect endpoint
 * 3. Logs the employer outreach apply event
 *
 * @returns Whether the flow completed successfully (for logging purposes)
 */
export async function handleEmployerOutreachApply({
  invite,
  rezcowBaseUrl,
  resumeBaseUrl,
  jobKey,
  outreachSource,
  outreachType,
  accountKey,
  ctk,
  mobtk,
}: HandleEmployerOutreachApplyParams): Promise<boolean> {
  let isError = false;

  try {
    const result = await callRezContactApi({
      rezcowBaseUrl,
      endpoint: "/api/v1/contacted/interested",
      engagement: invite,
    });
    isError = !result.success;
  } catch (error) {
    // If API call fails, mark as error but don't block the apply flow
    isError = true;
  }

  // Call apply redirect endpoint
  if (!isError) {
    try {
      const redirectParams = new URLSearchParams({
        rAKey: invite.recipient.id,
        sAKey: invite.sender.id,
        uid: invite.id,
        h: invite.conversation.key,
        jk: jobKey,
      });
      const redirectResult = await callRezContactGetApi({
        baseUrl: resumeBaseUrl,
        endpoint: "/contacted/apply/redirect",
        params: redirectParams,
        followRedirect: false,
        useJsonContentType: false,
      });
      if (!redirectResult.success) {
        isError = true;
      }
    } catch (error) {
      // If API call fails, mark as error but don't block the apply flow
      isError = true;
    }
  }

  logEmployerOutreachApply({
    employerOutreachSource: outreachSource,
    outreachType,
    accountKey,
    ctk,
    jobKey,
    mobtk,
    isError,
  });

  return !isError;
}
